import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { fetchProfile } from "../api/user";
import { fetchChainControl } from "../api/traffic";

// I-80 / US-50 chain-control widget, backed by the Caltrans CWWP2 poller (traffic-fetch.mjs).
// See CLAUDE.md -> Chain control data research spike findings.
const ROUTES = [
  { routeSlug: "i80", label: "I-80 · Donner Summit" },
  { routeSlug: "us50", label: "US-50 · Echo Summit" },
];

const STATUS_ICO_CLASS = {
  R1: "warn",
  R2: "warn",
  R3: "bad",
  closed: "bad",
  none: "none",
  unknown: "none",
};

const STATUS_STROKE = {
  warn: "var(--warn)",
  bad: "var(--bad)",
  none: "var(--ok)",
};

function ChainIcon({ tier }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={STATUS_STROKE[tier]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.5 6.5a4 4 0 015.66 5.66l-3 3a4 4 0 01-5.66 0" />
      <path d="M13.5 17.5a4 4 0 01-5.66-5.66l3-3a4 4 0 015.66 0" />
    </svg>
  );
}

function RouteCard({ route, status }) {
  const hasData = status && status.status !== "no_data";
  const tier = hasData ? (STATUS_ICO_CLASS[status.status] ?? "none") : "none";

  return (
    <div className="glass chain-card">
      <div className={`chain-ico ${tier}`}>
        <ChainIcon tier={tier} />
      </div>
      <div className="card-main">
        <div className="chain-title">{route.label}</div>
        {hasData ? (
          <>
            <div className="chain-sub">
              {status.statusLabel ?? status.status}
              {status.locationName && ` — near ${status.locationName}`}
            </div>
            <p className="mono updated-at">
              Updated {status.updatedAt ? new Date(status.updatedAt).toLocaleTimeString() : "—"}
            </p>
          </>
        ) : (
          <div className="chain-sub">No chain-control data fetched yet.</div>
        )}
      </div>
    </div>
  );
}

export function TrafficTab() {
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ["chainControl"],
    queryFn: () => Promise.all(ROUTES.map((r) => fetchChainControl(r.routeSlug))),
    refetchInterval: 5 * 60 * 1000,
  });
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.userId],
    queryFn: () => fetchProfile(user.userId),
    enabled: !!user?.userId,
  });

  if (isLoading) return <p>Loading traffic…</p>;
  if (error) return <p className="badge-warn">Couldn't load chain control.</p>;

  const isDemo = data?.some((s) => s?.isDemoData);

  return (
    <section>
      <h2>Traffic</h2>
      <p className="sec-dek">Chain control, refreshed every 15 minutes from Caltrans District 3.</p>
      {profile?.homeLocation === "tahoe_local" && (
        <p className="sec-dek">You're already up here — this is mostly for the drive-up crowd.</p>
      )}
      {isDemo && (
        <span className="badge-info demo-badge">
          Demo data — the season hasn't started. Real Caltrans chain control takes over automatically once
          it does.
        </span>
      )}
      <div className="route-grid">
        {ROUTES.map((route, i) => (
          <RouteCard key={route.routeSlug} route={route} status={data[i]} />
        ))}
      </div>
      <div className="glass when-to-leave">
        <h3>When to leave</h3>
        <p>
          Most people leave exactly when everyone else does — Saturday 6-9am and Sunday 2-5pm up the hill
          are the worst windows on both I-80 and US-50. Leaving before 6am or after 10am beats the pack on
          a typical powder weekend; coming back, before 1pm or after 6pm does the same. Chain control can
          still change fast during a storm regardless of timing — check back before you go.
        </p>
      </div>
    </section>
  );
}
